import React from "react";
import {Row, Col} from "react-bootstrap";
import {RBSModal} from "../../base/rbs_components/RBSModal.tsx";
import {RBSTextField} from "../../base/rbs_components/RBSTextField.tsx";
import {RBSDropdown} from "../../base/rbs_components/RBSDropdown.tsx";
import type {Trigger} from "../../base/ui_general/GeneralInterfaces.tsx";
import {ValidationSchemes, LabelTextMessageTypes, TextfieldInputTypes} from "../../base/Settings.tsx";
import {getLabelTextMessage} from "../../base/utils/GeneralUtilities.tsx";
import type {InputStateControl} from "../../base/utils/InputStateControl.tsx";
import type {UserLanguages} from "../../base/Settings.tsx";
import {TriggerTypeEnum} from "../../base/Settings.tsx";

//TODO: Add multi-language support

/**
 * Props for the TriggerEditModal component.
 */
interface TriggerEditModalProps {
    /** Whether the modal is visible. */
    show: boolean;
    /** The trigger to edit. */
    trigger: Trigger;
    /** Callback when submitting the modal form. */
    onSubmit: (triggerInput: Trigger) => void;
    /** Callback when closing the modal. */
    onClose: () => void;
    /** Whether the current form state is valid. */
    isValid: boolean;
    /** State control for input fields. */
    inputStateControl: InputStateControl;
    /** The user's selected language. */
    user_language: UserLanguages;
}

export const TriggerEditModal: React.FC<TriggerEditModalProps> = (props) => {
    const {trigger, inputStateControl, user_language} = props;

    const prefill = (key: keyof Trigger): string => {
        if (trigger && trigger[key] !== undefined && trigger[key] !== null) {
            return String(trigger[key]);
        }
        return "";
    };

    const headerTitle = trigger ? "Update Trigger" : "Create New Trigger";

    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size="lg"
            onClickPrimary={() => {
                const updatedTrigger: Trigger = {
                    ...props.trigger,
                    name: inputStateControl.getStringValueForKey("name") || "",
                    description: inputStateControl.getStringValueForKey("description") || "",
                    trigger_type: (inputStateControl.getStringValueForKey("trigger_type") as TriggerTypeEnum) || TriggerTypeEnum.IRREVERSIBLE
                };

                props.onSubmit(updatedTrigger);
                props.onClose();
            }}
        >
            <Row>
                <Col md={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NAME, props.user_language)}
                        input_key="name"
                        prefill={prefill("name")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={inputStateControl}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <RBSDropdown
                        label="Trigger Type"
                        input_key="trigger_type"
                        prefill={prefill("trigger_type") || TriggerTypeEnum.IRREVERSIBLE}
                        items={[
                            {label: "Irreversible", value: TriggerTypeEnum.IRREVERSIBLE},
                            {label: "Reversible", value: TriggerTypeEnum.REVERSIBLE}
                        ]}
                        input_state_control={inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.DESCRIPTION, props.user_language)}
                        input_key="description"
                        prefill={prefill("description")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                        multiline={true}
                        style={{ minHeight: 300 }}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
};
